/**
 * Client Detail Route (Story 3.8)
 *
 * Displays the client detail dashboard with header, quick actions,
 * integrations, reports, and data status sections.
 *
 * Route: /clients/$clientId
 */

import { useState, useRef, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowClockwise, Sparkle } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { apiGet } from "../../../lib/apiClient";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { useToast } from "../../../hooks/useToast";
import type { Client } from "../../../components/clients/types";
import { Toast } from "../../../components/clients/Toast";
import { ClientDetailHeader } from "../../../components/clients/ClientDetailHeader";
import { IntegrationsCard } from "../../../components/clients/IntegrationsCard";
import { EditClientSheet } from "../../../components/clients/EditClientSheet";
import { DeleteClientDialog } from "../../../components/clients/DeleteClientDialog";
import { ClientGoalsCard } from "../../../components/clients/ClientGoalsCard";
import { BudgetProgressCard } from "../../../components/clients/BudgetProgressCard";
import {
  ClientTabBar,
  isClientTabId,
  type ClientTabId,
} from "../../../components/clients/ClientTabBar";
import { ShareLinksTab } from "../../../components/clients/ShareLinksTab";
import { SchedulesCard } from "../../../components/clients/SchedulesCard";
import { TokenAuthFailedBanner } from "../../../components/clients/TokenAuthFailedBanner";
import { ResourceUnavailableBanner } from "../../../components/integrations/ResourceUnavailableBanner";
import { parseCatalogCount } from "../../../lib/parse-catalog-count";

/**
 * Search params for the client detail route (Story 32.1 AC #15).
 * Exported so routeTree.gen.ts can name the type.
 */
export interface ClientDetailSearch {
  /** Active tab — deep-link target for the Epic 32 banners (32-4/32-5). */
  tab?: ClientTabId;
  /** Story 32-3: OAuth redirect-flow result ("success" | "error") from the callback page. */
  oauth?: string;
  /** Story 32-3 (AC #11): cataloged-resource count — `> 1` opens the catalog picker. */
  catalogCount?: number;
  /** Story 32-3: comma-list of platforms the OAuth flow connected. */
  platforms?: string;
}

export const Route = createFileRoute("/_authenticated/clients/$clientId")({
  // Invalid/garbage values fall back to the default tab (no crash) by being
  // stripped from the search object entirely.
  validateSearch: (search: Record<string, unknown>): ClientDetailSearch => {
    const catalogCount = parseCatalogCount(search.catalogCount);
    return {
      ...(isClientTabId(search.tab) ? { tab: search.tab } : {}),
      ...(typeof search.oauth === "string" ? { oauth: search.oauth } : {}),
      ...(catalogCount !== undefined ? { catalogCount } : {}),
      ...(typeof search.platforms === "string"
        ? { platforms: search.platforms }
        : {}),
    };
  },
  component: ClientDetailPage,
});

function ClientDetailPage() {
  const { clientId } = Route.useParams();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  // Client tab navigation — URL-driven (Story 32.1 AC #15): the `?tab=`
  // search param is the single source of truth so the tab is deep-linkable
  // (e.g. /clients/{id}?tab=integrations from the 32-4/32-5 banners).
  const { tab, oauth, catalogCount, platforms } = Route.useSearch();
  const activeTab: ClientTabId = tab ?? "dashboards";
  const handleTabChange = (next: ClientTabId) => {
    navigate({
      from: Route.fullPath,
      search: { tab: next },
      replace: true,
    });
  };

  // Story 32-3 (AC #11), redirect-flow arm: an OAuth success that cataloged
  // more than one resource opens the CatalogPickerDialog so the user can
  // attach the additional discovered accounts. The picker shows the FIRST
  // connected platform's catalog (boring choice — sibling platforms remain a
  // tile-click away); params are stripped via replace-navigation so a page
  // refresh never re-opens it. catalogCount <= 1 / absent → nothing (today's
  // success behavior, byte-for-byte).
  const [pendingCatalogPlatform, setPendingCatalogPlatform] = useState<string | null>(null);
  useEffect(() => {
    if (oauth !== "success") return;
    const openPicker = (catalogCount ?? 0) > 1 && !!platforms;
    if (openPicker) {
      const firstPlatform = platforms!.split(",")[0];
      if (firstPlatform) setPendingCatalogPlatform(firstPlatform);
    }
    // Strip the one-shot oauth params (picker target lives in state). Only
    // the picker case switches to the integrations tab — otherwise the
    // landing tab stays exactly as today (no behavior change for
    // catalogCount <= 1 / absent).
    navigate({
      from: Route.fullPath,
      search: openPicker ? { tab: "integrations" } : tab ? { tab } : {},
      replace: true,
    });
  }, [oauth, catalogCount, platforms, tab, navigate]);

  // Shared EditClientSheet state (H2 fix — single instance for the whole page)
  const [editOpen, setEditOpen] = useState(false);
  const editTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Delete dialog state (Story 3.9 — single instance lifted to parent)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    data: client,
    isLoading,
    error,
    refetch,
  } = useQuery<Client>({
    queryKey: ["clients", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Failed to load client" }));
        throw new Error(err.detail ?? err.title ?? "Failed to load client");
      }
      const json = await res.json();
      return json.data;
    },
  });

  // Set document title
  useEffect(() => {
    const prevTitle = document.title;
    if (client) {
      document.title = `${client.name} - ProvenLeap`;
    }
    return () => {
      document.title = prevTitle;
    };
  }, [client]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/clients" });
    }
  };

  const handleEditClick = (triggerEl?: HTMLButtonElement | null) => {
    editTriggerRef.current = triggerEl ?? null;
    setEditOpen(true);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleted = () => {
    setDeleteDialogOpen(false);
    navigate({ to: "/clients", replace: true });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          <div className="space-y-8">
            <div className="h-48 bg-muted animate-pulse rounded-lg" />
            <div className="h-48 bg-muted animate-pulse rounded-lg" />
          </div>
          <div className="h-48 bg-muted animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  // Error state (M4 fix — added retry button)
  if (error) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div
            className="rounded-md border border-destructive bg-destructive/5 p-6 text-sm text-destructive max-w-md"
            role="alert"
          >
            <h2 className="text-lg font-semibold mb-2">Client not found</h2>
            <p className="mb-4">{error.message}</p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => refetch()}>
                <ArrowClockwise size={16} className="mr-2" />
                Try Again
              </Button>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft size={16} className="mr-2" />
                Back to Clients
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <motion.div
      className="pt-8 px-6 pb-6 max-w-[1800px] mx-auto"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <ReportBreadcrumb
        items={[
          { label: "Clients", to: "/clients" },
          { label: client.name },
        ]}
      />
      <div className="mt-4 flex items-center gap-4">
        <div className="flex-1">
          <ClientDetailHeader
            client={client}
            onBack={handleBack}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            showToast={showToast}
            variant="left"
          />
        </div>
        <ClientDetailHeader
          client={client}
          onBack={handleBack}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          showToast={showToast}
          variant="right"
        />
      </div>

      {/* Story 32-5 AC #13: per-token auth-failed banner between the header
          row and the ClientTabBar notch — visible on EVERY tab (this single
          mount covers the client dashboard AND today's connections surface,
          the Integrations tab). Always mounted: it self-nulls when empty so
          its AnimatePresence exit plays on auto-clear (§4.6). */}
      <TokenAuthFailedBanner
        clientId={clientId}
        showToast={showToast}
        onOpenIntegrations={() => handleTabChange("integrations")}
      />

      <div className="relative z-20 -mt-[36px] px-6 pointer-events-none">
        <ClientTabBar activeTab={activeTab} onTabChange={handleTabChange} />
      </div>

      {/* Story 32-4 AC #1/#12: per-resource unavailable banner between the
          ClientTabBar wrapper and the tab-content card — visible on EVERY
          tab. Stacking contract: 32-5's token-axis banner sits ABOVE the tab
          bar (fixes more at once); this resource-axis one sits below it.
          Always mounted — it self-nulls when empty so its AnimatePresence
          exit plays on auto-clear (§4.6). */}
      <ResourceUnavailableBanner
        clientId={clientId}
        clientName={client.name}
        showToast={showToast}
      />

      <div
        className="rounded-[22px] bg-background shadow-xs mt-2"
        style={{
          borderLeft: "0.5px solid var(--hairline)",
          borderRight: "0.5px solid var(--hairline)",
          borderBottom: "0.5px solid var(--hairline)",
          borderTop: "0.5px solid var(--hairline)",
        }}
      >
        <div className="p-6">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.08, ease: "easeOut" }}
            >
              {activeTab === "integrations" ? (
                <IntegrationsCard
                  clientId={clientId}
                  clientName={client?.name}
                  showToast={showToast}
                  initialCatalogPlatform={pendingCatalogPlatform}
                  onInitialCatalogHandled={() => setPendingCatalogPlatform(null)}
                />
              ) : activeTab === "client-portal" ? (
                <ShareLinksTab
                  clientId={clientId}
                  clientName={client.name}
                  showToast={showToast}
                />
              ) : activeTab === "schedules" ? (
                <SchedulesCard
                  clientId={clientId}
                  clientTimezone={client.timezone}
                  showToast={showToast}
                />
              ) : activeTab === "dashboards" ? (
                // Card stack — the 18-2 BudgetProgressCard sits beside the
                // goals card (Story 18.2 AC #6). Placeholder retained below for
                // the rest of the future dashboard (Epic 8's surface).
                <div className="space-y-6">
                  <BudgetProgressCard
                    clientId={clientId}
                    clientName={client?.name}
                    showToast={showToast}
                  />
                  <ClientGoalsCard clientId={clientId} />
                  <ComingSoonPlaceholder tabLabel="Dashboards" />
                </div>
              ) : (
                <ComingSoonPlaceholder tabLabel="Reports" />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <EditClientSheet
        client={editOpen ? client : null}
        open={editOpen}
        onOpenChange={setEditOpen}
        triggerRef={editTriggerRef}
      />

      <DeleteClientDialog
        client={deleteDialogOpen ? { id: client.id, name: client.name } : null}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={handleDeleted}
        showToast={showToast}
      />

      <Toast toast={toast} />
    </motion.div>
  );
}

function ComingSoonPlaceholder({ tabLabel }: { tabLabel: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-4 rounded-full bg-muted p-4">
        <Sparkle size={32} weight="duotone" className="text-muted-foreground" />
      </div>
      <h2 className="text-lg font-semibold mb-1">{tabLabel} — Coming Soon</h2>
      <p className="text-sm text-muted-foreground max-w-sm">
        We&apos;re building this section. Stay tuned for updates.
      </p>
    </div>
  );
}
